import {useState} from "react";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {Button, Stack, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import {CompanyMemberInviteDialog} from "../dialogs/CompanyMemberInviteDialog";
import {CompanyMemberBulkInviteDialog} from "../dialogs/CompanyMemberBulkInviteDialog";
import {CompanyMembersTable} from "./CompanyMembersTable";
import {CompanyMemberEditDialog} from "../dialogs/CompanyMemberEditDialog";
import {useActiveCompanyMemberItems} from "../../hooks/activeCompanyMemberItemsHooks";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ViewAuthorizationGuard} from "../ViewAuthorizationGuard";
import {MainViewLayout} from "../MainViewLayout";
import {CompanyHeader} from "./CompanyHeader";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useCompany} from "../../hooks/companiesHooks";

export const CompanyMembersView: React.FC = () => {
    const {t} = useTranslation();
    const [inviteMemberDialogOpened, setInviteMemberDialogOpened] = useState<boolean>(false);
    const [addMemberDialogOpened, setAddMemberDialogOpened] = useState<boolean>(false);
    const [bulkInviteMemberDialogOpened, setBulkInviteMemberDialogOpened] = useState<boolean>(false);

    const companyState = useCompany();
    const company = companyState.isSuccess && companyState.data !== undefined ? companyState.data : undefined;

    const companyMemberItemsState = useActiveCompanyMemberItems();

    const onInviteMemberDialogClose = (isSuccessful: boolean) => {
        setInviteMemberDialogOpened(false);
        if (isSuccessful) {
            companyMemberItemsState.refresh();
        }
    };

    const onAddMemberDialogClose = (isSuccessful: boolean) => {
        setAddMemberDialogOpened(false);
        if (isSuccessful) {
            companyMemberItemsState.refresh();
        }
    };

    const onBulkInviteMemberDialogClose = (isSuccessful: boolean) => {
        setBulkInviteMemberDialogOpened(false);
        if (isSuccessful) {
            companyMemberItemsState.refresh();
        }
    };

    const access = useAccessMarker(ACCESS_FUNCTIONS);

    return (
        <ViewAuthorizationGuard isAuthorized={access.canViewCompanyMembers}>
            <MainViewLayout header={<CompanyHeader basePath="/organization" />}>
                <Stack direction="column">
                    <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            {t("companies.members_view.users")}
                        </Typography>
                        <Stack direction="row">
                            {access.canAddCompanyMember && (
                                <Button startIcon={<AddIcon />} onClick={() => setAddMemberDialogOpened(true)}>
                                    {t("companies.members_view.add")}
                                </Button>
                            )}
                            <Button startIcon={<AddIcon />} onClick={() => setInviteMemberDialogOpened(true)}>
                                {t("companies.members_view.invite")}
                            </Button>
                            {access.canBulkInviteCompanyMembers && (
                                <Button startIcon={<AddIcon />} onClick={() => setBulkInviteMemberDialogOpened(true)}>
                                    {t("companies.members_view.bulk_invite")}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                    <CompanyMembersTable
                        companyId={company?.id}
                        members={companyMemberItemsState.memberItems}
                        isLoading={companyMemberItemsState.isLoading}
                        refreshMembers={companyMemberItemsState.refresh}
                    />
                </Stack>
                {addMemberDialogOpened && access.canAddCompanyMember && company !== undefined && (
                    <CompanyMemberEditDialog
                        mode="create"
                        managerRoleEnabled={access.canAssignManagerRole}
                        companyId={company.id}
                        open={addMemberDialogOpened}
                        onClose={onAddMemberDialogClose}
                    />
                )}
                {inviteMemberDialogOpened && company !== undefined && (
                    <CompanyMemberInviteDialog
                        mode="create"
                        companyId={company.id}
                        open={inviteMemberDialogOpened}
                        onClose={onInviteMemberDialogClose}
                    />
                )}
                {bulkInviteMemberDialogOpened && access.canBulkInviteCompanyMembers && company !== undefined && (
                    <CompanyMemberBulkInviteDialog
                        companyId={company.id}
                        open={bulkInviteMemberDialogOpened}
                        onClose={onBulkInviteMemberDialogClose}
                    />
                )}
            </MainViewLayout>
        </ViewAuthorizationGuard>
    );
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, [
    "canViewCompanyMembers",
    "canAddCompanyMember",
    "canBulkInviteCompanyMembers",
    "canAssignManagerRole",
]);
